import { type ReactNode, useState, useRef, useEffect } from 'react';
import { Search, List, LayoutGrid, Columns, SlidersHorizontal, X } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════
   DataToolbar — Barra universal de busca, ações e filtros de dados
   Responsiva:
     - Desktop: Busca fixa + botões à esquerda | registros + página + modos à direita
     - Mobile: Lupa compacta à esquerda | Carrossel horizontal de botões no centro | Botão de ajustes à direita
   ═══════════════════════════════════════════════════════════════ */

export type ViewMode = 'lista' | 'cards' | 'kanban';

const VIEW_ICONS: Record<ViewMode, typeof List> = {
  lista: List,
  cards: LayoutGrid,
  kanban: Columns,
};

const VIEW_TITLES: Record<ViewMode, string> = {
  lista: 'Lista',
  cards: 'Cards',
  kanban: 'Kanban (Funil)',
};

export interface DataToolbarProps {
  /** Valor da busca */
  busca?: string;
  /** Handler de busca */
  onBuscaChange?: (value: string) => void;
  /** Placeholder de busca */
  placeholder?: string;
  /** Botões de ação principais (ex: "+ Adicionar", "Assistente BNCC", etc.) */
  actions?: ReactNode;
  /** Total de registros (exibido como texto informativo) */
  totalRecords?: number;
  /** Texto personalizado de contagem em substituição ao totalRecords */
  infoText?: string;
  /** Quantidade de itens por página */
  pageSize?: number;
  /** Handler de alteração de itens por página */
  onPageSizeChange?: (size: number) => void;
  /** Opções de itens por página (padrão: [6, 12, 24, 48]) */
  pageSizeOptions?: number[];
  /** Modo de visualização ativo */
  viewMode?: ViewMode;
  /** Handler de alteração do modo de visualização */
  onViewModeChange?: (mode: ViewMode) => void;
  /** Modos de visualização permitidos */
  viewModes?: ViewMode[];
  /** Elementos de filtro adicionais (selects de ano, turma, status, etc.) */
  filters?: ReactNode;
  /** Elementos extras no lado direito */
  extra?: ReactNode;
  /** Se true, oculta completamente o campo de busca */
  hideSearch?: boolean;
  /** Centraliza filtros no desktop (ex: kanban) */
  centerFilters?: boolean;
  /** Classes CSS adicionais */
  className?: string;
}

export function DataToolbar({
  busca = '',
  onBuscaChange,
  placeholder = 'Buscar...',
  actions,
  totalRecords,
  infoText,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [6, 12, 24, 48],
  viewMode,
  onViewModeChange,
  viewModes = ['lista', 'cards'],
  filters,
  extra,
  hideSearch = false,
  centerFilters = false,
  className = '',
}: DataToolbarProps) {
  const displayInfo = infoText || (totalRecords !== undefined ? `${totalRecords} registro(s)` : undefined);

  /* ── Mobile state ── */
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(Boolean(busca && busca.trim().length > 0));
  const [isMobileSettingsOpen, setIsMobileSettingsOpen] = useState(false);
  const mobileSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isMobileSearchOpen && mobileSearchRef.current) {
      mobileSearchRef.current.focus();
    }
  }, [isMobileSearchOpen]);

  /* ── Desktop collapsible search (específico para kanban) ── */
  const [kanbanSearchOpen, setKanbanSearchOpen] = useState(false);
  const kanbanSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (kanbanSearchOpen && kanbanSearchRef.current) {
      kanbanSearchRef.current.focus();
    }
  }, [kanbanSearchOpen]);

  const isKanbanCollapsible = centerFilters && !hideSearch && onBuscaChange;

  return (
    <div className={`dt-toolbar-container w-full border-b border-[var(--border-color,#e5e7eb)] bg-[var(--bg-main,#f8fafc)] ${className}`}>
      {/* ───────────────────────────────────────────────────────────
          1. VISUALIZAÇÃO DESKTOP (hidden md:flex)
          ─────────────────────────────────────────────────────────── */}
      <div className="hidden md:flex items-center justify-between gap-3 px-3.5 py-2.5 w-full flex-wrap">
        {/* Lado Esquerdo: Busca + Ações + Filtros */}
        <div className="flex items-center gap-2.5 flex-wrap shrink-0">
          {!hideSearch && onBuscaChange && !isKanbanCollapsible && (
            <div className="dt-search relative flex items-center">
              <Search size={14} className="dt-search-icon absolute left-2.5 text-[var(--text-muted,#64748b)] pointer-events-none" />
              <input
                value={busca || ''}
                onChange={(e) => onBuscaChange(e.target.value)}
                placeholder={placeholder}
                className="dt-search-input h-[34px] w-[260px] lg:w-[280px] rounded-lg border border-[var(--border-color,#e5e7eb)] pl-8 pr-3 text-xs bg-[var(--bg-card,white)] text-[var(--text-main,#1e293b)] focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-sm"
              />
              {busca && (
                <button
                  type="button"
                  onClick={() => onBuscaChange('')}
                  className="absolute right-2 text-slate-400 hover:text-slate-600 p-0.5"
                  title="Limpar busca"
                >
                  <X size={13} />
                </button>
              )}
            </div>
          )}

          {/* Collapsible search para Kanban */}
          {isKanbanCollapsible && !kanbanSearchOpen && (
            <button
              type="button"
              className="kb-search-toggle h-[34px] px-3 rounded-lg border border-[var(--border-color,#e5e7eb)] bg-[var(--bg-card,white)] text-[var(--text-muted,#64748b)] hover:text-blue-600 flex items-center justify-center shadow-sm"
              onClick={() => setKanbanSearchOpen(true)}
              title="Buscar"
            >
              <Search size={15} />
            </button>
          )}

          {isKanbanCollapsible && kanbanSearchOpen && (
            <div className="dt-search relative flex items-center">
              <Search size={14} className="dt-search-icon absolute left-2.5 text-[var(--text-muted,#64748b)] pointer-events-none" />
              <input
                ref={kanbanSearchRef}
                value={busca || ''}
                onChange={(e) => onBuscaChange(e.target.value)}
                placeholder={placeholder}
                className="dt-search-input h-[34px] w-[260px] rounded-lg border border-[var(--border-color,#e5e7eb)] pl-8 pr-3 text-xs bg-[var(--bg-card,white)] text-[var(--text-main,#1e293b)] focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-sm"
                onBlur={() => { if (!busca) setKanbanSearchOpen(false); }}
                onKeyDown={(e) => { if (e.key === 'Escape') { onBuscaChange(''); setKanbanSearchOpen(false); } }}
              />
            </div>
          )}

          {/* Botões de Ação */}
          {actions && <div className="flex items-center gap-2">{actions}</div>}

          {/* Filtros à esquerda quando não centralizados */}
          {!centerFilters && filters && <div className="flex items-center gap-2">{filters}</div>}
        </div>

        {/* Centro: Filtros centralizados (Kanban) */}
        {centerFilters && filters && (
          <div className="flex flex-col items-center justify-center flex-1 min-w-0">
            {filters}
          </div>
        )}

        {/* Lado Direito: Info + Seletor de Página + Extra + Modos de Visualização */}
        <div className="flex items-center gap-2.5 shrink-0">
          {displayInfo && (
            <span className="text-xs text-[var(--text-muted,#64748b)] font-medium select-none">
              {displayInfo}
            </span>
          )}

          {pageSize !== undefined && onPageSizeChange && (
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="h-[32px] rounded-lg border border-[var(--border-color,#e5e7eb)] px-2.5 text-xs bg-[var(--bg-card,white)] text-[var(--text-main,#1e293b)] focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-sm cursor-pointer"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>
                  {n}/página
                </option>
              ))}
            </select>
          )}

          {extra}

          {viewMode && onViewModeChange && viewModes.length > 1 && (
            <div className="flex border border-[var(--border-color,#e5e7eb)] rounded-lg overflow-hidden shadow-sm bg-[var(--bg-card,white)]">
              {viewModes.map((mode, i) => {
                const Icon = VIEW_ICONS[mode];
                const isActive = viewMode === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => onViewModeChange(mode)}
                    title={VIEW_TITLES[mode]}
                    className={`h-[32px] w-[36px] flex items-center justify-center transition-colors ${
                      i > 0 ? 'border-l border-[var(--border-color,#e5e7eb)]' : ''
                    } ${
                      isActive
                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400 font-bold'
                        : 'text-[var(--text-muted,#64748b)] hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    }`}
                  >
                    <Icon size={14} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ───────────────────────────────────────────────────────────
          2. VISUALIZAÇÃO MOBILE (md:hidden)
          ─────────────────────────────────────────────────────────── */}
      <div className="flex md:hidden flex-col w-full">
        {/* Linha Superior: Busca Retrátil | Carrossel de Ações | Botão de Ajustes */}
        <div className="flex items-center gap-2 px-2 py-1.5 w-full justify-between relative min-h-[44px]">
          {isMobileSearchOpen && !hideSearch && onBuscaChange ? (
            /* Busca Aberta no Mobile (Ocupa a linha toda) */
            <div className="flex-1 flex items-center gap-2 bg-[var(--bg-card,white)] border border-blue-500/80 rounded-lg px-2.5 py-1 shadow-inner animate-in fade-in zoom-in-95 duration-150">
              <Search size={14} className="text-blue-500 shrink-0" />
              <input
                ref={mobileSearchRef}
                value={busca || ''}
                onChange={(e) => onBuscaChange(e.target.value)}
                placeholder={placeholder}
                className="flex-1 bg-transparent text-xs text-[var(--text-main,#1e293b)] outline-none min-w-0"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    if (!busca) setIsMobileSearchOpen(false);
                  }
                }}
              />
              <button
                type="button"
                onClick={() => {
                  onBuscaChange('');
                  setIsMobileSearchOpen(false);
                }}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-md shrink-0 active:scale-90 transition-transform"
                title="Fechar busca"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            /* Barra Compacta: Lupa + Scroll Horizontal de Botões + Ajustes */
            <>
              {/* Botão de Lupa (Esquerda) */}
              {!hideSearch && onBuscaChange && (
                <button
                  type="button"
                  onClick={() => setIsMobileSearchOpen(true)}
                  className={`h-8 w-8 rounded-lg border flex items-center justify-center shrink-0 transition-all active:scale-95 relative ${
                    busca && busca.trim().length > 0
                      ? 'border-blue-500 bg-blue-50 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400'
                      : 'border-[var(--border-color,#e5e7eb)] bg-[var(--bg-card,white)] text-[var(--text-muted,#64748b)] hover:text-blue-600'
                  }`}
                  title="Buscar"
                >
                  <Search size={14} />
                  {busca && busca.trim().length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-blue-600 ring-2 ring-white" />
                  )}
                </button>
              )}

              {/* Centro: Carrossel Horizontal de Botões (Sem quebra de linha) */}
              <div
                className="flex-1 flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-nowrap min-w-0 py-0.5"
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                {actions}
              </div>

              {/* Botão de Ajustes (Direita) */}
              {(displayInfo || onPageSizeChange || (viewMode && onViewModeChange) || filters || extra) && (
                <button
                  type="button"
                  onClick={() => setIsMobileSettingsOpen(!isMobileSettingsOpen)}
                  className={`h-8 w-8 rounded-lg border flex items-center justify-center shrink-0 transition-all active:scale-95 ${
                    isMobileSettingsOpen
                      ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                      : 'border-[var(--border-color,#e5e7eb)] bg-[var(--bg-card,white)] text-[var(--text-muted,#64748b)] hover:text-blue-600'
                  }`}
                  title="Configurações e Visualização"
                >
                  <SlidersHorizontal size={14} />
                </button>
              )}
            </>
          )}
        </div>

        {/* Linha Inferior: Gaveta Retrátil de Ajustes e Visualização */}
        {isMobileSettingsOpen && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-2.5 py-2 border-t border-[var(--border-color,#e5e7eb)] bg-[var(--bg-card,white)]/90 backdrop-blur-sm animate-in slide-in-from-top-1 duration-150 text-xs select-none">
            {/* Esquerda: Registros + Seletor de Página */}
            <div className="flex items-center gap-2">
              {displayInfo && (
                <span className="text-[11px] text-[var(--text-muted,#64748b)] font-semibold">
                  {displayInfo}
                </span>
              )}

              {pageSize !== undefined && onPageSizeChange && (
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="h-7 rounded-md border border-[var(--border-color,#e5e7eb)] px-2 text-[11px] bg-[var(--bg-main,#f8fafc)] text-[var(--text-main,#1e293b)] focus:outline-none focus:ring-1 focus:ring-blue-500 font-medium"
                >
                  {pageSizeOptions.map((n) => (
                    <option key={n} value={n}>
                      {n}/pág
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Filtros extras no mobile */}
            {filters && <div className="flex items-center gap-1.5">{filters}</div>}

            {/* Direita: Modos de Visualização + Extra */}
            <div className="flex items-center gap-2 ml-auto">
              {extra}

              {viewMode && onViewModeChange && viewModes.length > 1 && (
                <div className="flex border border-[var(--border-color,#e5e7eb)] rounded-md overflow-hidden bg-[var(--bg-main,#f8fafc)]">
                  {viewModes.map((mode, i) => {
                    const Icon = VIEW_ICONS[mode];
                    const isActive = viewMode === mode;
                    return (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => onViewModeChange(mode)}
                        title={VIEW_TITLES[mode]}
                        className={`h-7 w-8 flex items-center justify-center transition-colors ${
                          i > 0 ? 'border-l border-[var(--border-color,#e5e7eb)]' : ''
                        } ${
                          isActive
                            ? 'bg-blue-600 text-white font-bold'
                            : 'text-[var(--text-muted,#64748b)] hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`}
                      >
                        <Icon size={13} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
